import { Badge, Icon } from "@platform/ui/components";
import * as React from "react";
import { z } from "zod";
import { Banner, BannerCloseAction } from "~/shared/components/ds/Banner";
import { getSessionStorageValue, useSessionStorage } from "~/shared/hooks/useSessionStorage";

export function Impersonation() {
  const { value, handlers } = useImpersonationState();
  const { remove } = handlers;

  if (value == null) return null;

  return (
    <Banner className="border-ds-accent-primary-9 sticky top-0 left-(--sidebar-width) z-30 h-(--siteheader-position-y) w-[calc(100%-var(--sidebar-width))]">
      <span className="flex items-center gap-[1ch]">
        <Badge
          className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full rounded-t-none"
          variant="solid"
          colorScheme="primary"
          icon={<Icon symbol="id-card-fill" />}
        >
          Impersonating
        </Badge>
        {TYPE_TO_TITLE[value.type]} &mdash; {value.firstName} {value.lastName}
        <BannerCloseAction onClick={remove} />
      </span>
    </Banner>
  );
}

const TYPE_TO_TITLE = {
  recruiter: "Recruiter",
  participant: "Participant",
} satisfies Record<z.infer<typeof ImpersonationSchema>["type"], string>;

export function useImpersonationState() {
  const [value, setValue, removeValue] = useSessionStorage(IMPERSONATION_KEY, null, {
    deserializer: (value) => {
      return ImpersonationSchema.parse(JSON.parse(value));
    },
  });

  const handlers = React.useMemo(() => {
    return {
      setValue: (value: z.infer<typeof ImpersonationSchema>) => {
        setValue(value);
        window.location.replace("/");
      },
      remove: () => {
        removeValue();
        window.location.replace("/");
      },
    };
  }, [removeValue, setValue]);

  return React.useMemo(() => {
    return {
      value,
      handlers,
    };
  }, [handlers, value]);
}

const ImpersonationSchema = z.object({
  id: z.coerce.number(),
  type: z.union([z.literal("recruiter"), z.literal("participant")]),
  firstName: z.string(),
  lastName: z.string(),
  username: z.string(),
});

export const IMPERSONATION_KEY = "BL_MD" as const;

export function getImpersonationState() {
  const sessionStorageValue = getSessionStorageValue(IMPERSONATION_KEY, ImpersonationSchema);

  // Check if we have the value in sessionStorage
  if (sessionStorageValue) {
    return sessionStorageValue;
  }

  // Fallback to localStorage (for backwards compatibility) and migrate if found
  try {
    const localStorageValue = window.localStorage.getItem(IMPERSONATION_KEY);
    if (localStorageValue) {
      const parsedValue = ImpersonationSchema.parse(JSON.parse(localStorageValue));

      // Migrate to sessionStorage and remove from localStorage
      window.sessionStorage.setItem(IMPERSONATION_KEY, localStorageValue);
      window.localStorage.removeItem(IMPERSONATION_KEY);

      return parsedValue;
    }
  } catch (error) {
    // If parsing fails, continue
  }

  return null;
}
